import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { delay, finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from '../api.service';
import { Router } from '@angular/router';
import { apiData } from '../api-lookup';

export interface RefreshTokenResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
}

const LogoutDevice = apiData[327].LogoutDevice;
const refresh = apiData[449].refresh
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly baseUrl = environment.apiBaseUrl;
  redirect: string;
  private accessToken: string | null = null;
  private accessTokenExpiry: number | null = null;

  private refreshTimer: any;

  // Reused so a proactive (timer) refresh and a reactive (401) refresh
  // that fire at the same moment share one HTTP call instead of racing.
  private refreshInProgress$: Observable<RefreshTokenResponse> | null = null;

  private authState$ =
    new BehaviorSubject<boolean>(false);

  // HttpClient that bypasses AuthInterceptor
  private refreshHttp: HttpClient;

  constructor(
    private http: HttpClient,
    private router: Router,
    private apiService: ApiService,
    httpBackend: HttpBackend
  ) {

    // IMPORTANT:
    // This HttpClient does NOT use AuthInterceptor
    this.refreshHttp = new HttpClient(httpBackend);

    this.validateSessionOnStartup();
  }

  // ============================================================
  // LOGIN
  // ============================================================

  login(token: string): Observable<boolean> {

    return of(true).pipe(

      delay(1000),

      tap(() => {

        this.setAccessToken(token);

        this.authState$.next(true);
      })
    );
  }

  // ============================================================
  // REFRESH TOKEN
  // ============================================================

  refreshToken(): Observable<RefreshTokenResponse> {

    // If a refresh is already in flight (proactive timer and a reactive
    // 401 can both trigger this at once), reuse it instead of firing a
    // second call — the caller that started it, and everyone piggy-backing
    // on it, share the same response.
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    



    const lastToken =
      this.accessToken ??
      sessionStorage.getItem('authToken');

    if (!lastToken) {

   

      return throwError(
        () => new Error('No token available to refresh')
      );
    }

    const requestBody = {
      Token: lastToken
    };

   

    this.refreshInProgress$ = this.refreshHttp.post<RefreshTokenResponse>(
      `${refresh}`,
      requestBody
    ).pipe(

      tap((response) => {

        if (!response?.accessToken) {

          throw new Error(
            'Access token not returned from refresh API'
          );
        }

        this.setAccessToken(
          response.accessToken
        );

      }),

      // Once settled (success or failure), clear the shared in-flight
      // observable so the *next* refresh actually calls the API again.
      finalize(() => {
        this.refreshInProgress$ = null;
      }),

      shareReplay(1)
    );

    return this.refreshInProgress$;
  }

  // ============================================================
  // LOGOUT
  // ============================================================

  logout(): void {

    const token =
      this.accessToken ??
      sessionStorage.getItem('authToken');

    // Revoke the local session synchronously, before the server call.
    // Otherwise the proactive refresh timer (or a 401 on a request that
    // slips in while this call is pending) can still use the about-to-be
    // revoked token and refresh right under the logout.
    this.clearTokens();

    this.authState$.next(false);

    const requestBody = {
      Token: token
    };

    this.apiService
      .httpPost(`${LogoutDevice}`, requestBody)
      .subscribe({

        next: () => {
          this.finishLogoutCleanup();
        },

        error: (error: any) => {

          console.error(
            'LogoutDevice API Error:',
            error
          );

          this.finishLogoutCleanup();
        }
      });
  }

  // ============================================================
  // LOGOUT CLEANUP
  // ============================================================

  private finishLogoutCleanup(): void {

    const savedPolicy =
      sessionStorage.getItem('PasswordPolicy');

    sessionStorage.clear();

    if (savedPolicy) {

      sessionStorage.setItem(
        'PasswordPolicy',
        savedPolicy
      );
    }

    this.router.navigate([
      '/authentication/login'
    ]);
  }

  // ============================================================
  // SET ACCESS TOKEN
  // ============================================================

  setAccessToken(token: string): void {

    if (!token) {
      return;
    }

    this.accessToken = token;

    // Keep your current application behavior
    sessionStorage.setItem(
      'authToken',
      token
    );

    try {

      const payload = JSON.parse(
        atob(token.split('.')[1])
      );

      this.accessTokenExpiry =
        payload.exp * 1000;

 

    } catch (error) {

      console.warn(
        'Could not decode token expiry'
      );
    }

    // Reschedules the proactive refresh against the freshly decoded
    // expiry above (a no-op if decoding failed and it's still null).
    this.startProactiveRefresh();
  }

  // ============================================================
  // GET ACCESS TOKEN
  // ============================================================

  getAccessToken(): string | null {

    // Do not self-invalidate on the client's clock here: a late/throttled
    // proactive-refresh timer (e.g. a backgrounded tab) would otherwise make
    // this return null right when a request is about to go out, stripping
    // the Authorization header entirely and forcing the request through an
    // unauthenticated path instead of the normal 401 -> refresh -> retry
    // flow in AuthInterceptor. The server is the authority on expiry; let
    // a stale token be sent and rejected there, where refresh-and-retry
    // already handles it uniformly.
    return this.accessToken;
  }

  // ============================================================
  // IS LOGGED IN
  // ============================================================

  isLoggedIn(): boolean {

    return this.getAccessToken() !== null;
  }

  // ============================================================
  // USER TYPE
  // ============================================================

  getUserType(): string | null {

    const encodedUserType =
      sessionStorage.getItem('UserType');

    if (!encodedUserType) {
      return null;
    }

    try {

      return JSON.parse(
        atob(encodedUserType)
      );

    } catch (error) {

      console.warn(
        'Could not decode user type'
      );

      return null;
    }
  }

  // ============================================================
  // AUTH STATE
  // ============================================================

  getAuthState(): Observable<boolean> {

    return this.authState$.asObservable();
  }

  // ============================================================
  // PROACTIVE REFRESH
  // ============================================================

  // Refresh this many ms before the token's real expiry, not on a fixed
  // wall-clock cadence — the reference-token refresh only succeeds while
  // the access token being sent back is still valid.
  private static readonly PROACTIVE_REFRESH_BUFFER_MS = 60 * 1000;

  private startProactiveRefresh(): void {

    // A new token means a new expiry — always reschedule against it
    // instead of letting a stale interval run on.
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (!this.accessTokenExpiry) {
      return;
    }

    const refreshDelay = Math.max(
      this.accessTokenExpiry - Date.now() - AuthService.PROACTIVE_REFRESH_BUFFER_MS,
      0
    );

    this.refreshTimer = setTimeout(() => {

      this.refreshToken().subscribe({

        next: () => {

        },

        error: (error) => {

          console.error(
            '❌ Proactive refresh failed:',
            error
          );

          // Do not immediately logout here.
          // Interceptor can handle the next 401.
        }
      });

    }, refreshDelay);
  }

  // ============================================================
  // STARTUP SESSION
  // ============================================================

  private validateSessionOnStartup(): void {

    const lastToken =
      sessionStorage.getItem('authToken');

    if (!lastToken) {



      this.authState$.next(false);

      return;
    }

    this.refreshToken().subscribe({

      next: () => {

        this.authState$.next(true);

      },

      error: (error) => {

 

        this.clearTokens();

        this.authState$.next(false);
      }
    });
  }

  // ============================================================
  // CLEAR
  // ============================================================

  private clearTokens(): void {

    this.accessToken = null;

    this.accessTokenExpiry = null;

    if (this.refreshTimer) {

      clearTimeout(
        this.refreshTimer
      );

      this.refreshTimer = null;
    }

    this.refreshInProgress$ = null;

    // Without this, authGuard (which only checks that this key exists)
    // and refreshToken()'s sessionStorage fallback would keep trusting a
    // token this service has already given up on, letting a dead session
    // limp through a route guard only to fail on the next API call.
    sessionStorage.removeItem('authToken');

  }

  // ============================================================
  // UNAUTHORIZED
  // ============================================================

  handleUnauthorized(): void {



    this.clearTokens();

    this.authState$.next(false);
  }
}